import { z } from 'zod';
import { BaseErrorSchema } from '../../shared/error.schema';
import { ObjectIdSchema } from '../../shared/object-id.schema';
import { BLIKSY_UPLOAD_KEY_REGEX, ChatAttachmentSchema, ChatUploadPresignRequestSchema, ChatUploadPresignResponseSchema, MAX_CHAT_ATTACHMENTS_PER_MESSAGE } from '../../shared/chat-attachment';
export var BliksyMessageRole = /*#__PURE__*/ function(BliksyMessageRole) {
    BliksyMessageRole["system"] = "system";
    BliksyMessageRole["assistant"] = "assistant";
    BliksyMessageRole["user"] = "user";
    BliksyMessageRole["tool"] = "tool";
    return BliksyMessageRole;
}({});
/**
 * The session a Bliksy turn belongs to. It is the STEP RECORDING's id — Bliksy
 * sessions are not a separate document — which is why the same value scopes an
 * upload key.
 */ export const BliksySessionIdSchema = ObjectIdSchema;
/**
 * A document a Bliksy turn asks the model to read.
 *
 * The shared attachment claim, narrowed to the keys the BLIKSY presign endpoint
 * mints: an app-chat or legacy key is a well-formed chat upload of the same user
 * and is refused here anyway, because an upload is entitled by the surface it
 * was minted under.
 */ export const BliksyChatAttachmentSchema = ChatAttachmentSchema.extend({
    key: z.string().regex(BLIKSY_UPLOAD_KEY_REGEX, 'Invalid Bliksy upload key')
});
/**
 * What the client declares before it may upload — the shared presign request
 * plus the session whose authorization decides the key's recording segment.
 *
 * `sessionId` is in the BODY rather than the path because the Bliksy chat route
 * carries it there too; one shape for both keeps the guard chain identical.
 */ export const PresignBliksyUploadRequestSchema = ChatUploadPresignRequestSchema.extend({
    sessionId: BliksySessionIdSchema
});
/**
 * The shared presign envelope (see {@link ChatUploadPresignResponseSchema}),
 * narrowed to the key shape THIS surface mints.
 */ export const PresignBliksyUploadResponseSchema = ChatUploadPresignResponseSchema.extend({
    key: z.string().regex(BLIKSY_UPLOAD_KEY_REGEX, 'Invalid Bliksy upload key')
});
/**
 * Chat with Bliksy Request Schema
 *
 * Single message pattern: Frontend sends only the new message, backend loads
 * history from database. This keeps payload size constant regardless of
 * conversation length.
 */ export const ChatWithBliksyRequestSchema = z.object({
    sessionId: BliksySessionIdSchema,
    message: z.object({
        id: z.string(),
        content: z.string()
    }),
    /**
     * Documents the turn asks the model to read, referenced by the storage keys
     * the Bliksy presign endpoint minted. Shape-validated here; the backend still
     * re-derives ownership from the caller's claims and re-inspects the bytes.
     */ attachments: z.array(BliksyChatAttachmentSchema).max(MAX_CHAT_ATTACHMENTS_PER_MESSAGE).optional()
})// A turn is text, documents, or both — never neither. An empty text part is
// dropped before the model sees it, so a document-only turn is well-formed.
.refine((request)=>request.message.content.trim().length > 0 || (request.attachments?.length ?? 0) > 0, {
    message: 'A message needs text or an attachment',
    path: [
        'message',
        'content'
    ]
});
// Response is now a stream - no schema needed
export const ChatWithBliksyErrorSchema = BaseErrorSchema;

//# sourceMappingURL=chat-with-bliksy.schema.js.map